/**
 * Turret combat. Turrets mount on the Mk II hull's 4 ports, auto-aim at the
 * nearest NPC, and fire twin laser bolts. Mount offsets are in MTS_2 texture
 * pixels relative to the sprite centre (391×512); +x = right, +y = toward the
 * drill (forward). They map to the world via the ship's heading + hull scale.
 */
import { NPC, TURRET } from "@/config/balance";

/** Anything the player's turrets can lock onto and damage (neutral NPCs + enemies). */
export interface Damageable {
  pos: { x: number; y: number };
  hp: number;
  shield: number;
  shieldFlash: number;
  hitTimer: number;
}

// Mounts are texture-pixel offsets from the sprite centre (both hull textures are
// 512 tall). Left-side mounts (ox<0) sweep the left 180°, right-side the right.
// Mk II uses its own 4 ports (MTS_2); Mk III its 8 ports (MTS_3).
export const TURRET_MOUNTS_MK2 = [
  { ox: -87.5, oy: -198 }, // rear-left
  { ox: 87.5, oy: -198 }, // rear-right
  { ox: -149.5, oy: -74 }, // mid-left
  { ox: 149.5, oy: -74 }, // mid-right
];
export const TURRET_MOUNTS_MK3 = [
  { ox: -49, oy: -150 }, // rear-left
  { ox: 49, oy: -150 }, // rear-right
  { ox: -49, oy: -80 }, // mid-rear-left
  { ox: 49, oy: -80 }, // mid-rear-right
  { ox: -44, oy: 46 }, // mid-fore-left
  { ox: 44, oy: 46 }, // mid-fore-right
  { ox: -33, oy: 160 }, // nose-left
  { ox: 33, oy: 160 }, // nose-right
];
// Mk IV side ports (measured on MTS_4, 257×512) — 8 turrets flanking the 3
// central cannon slots.
export const TURRET_MOUNTS_MK4 = [
  { ox: -88, oy: -145 },
  { ox: 88, oy: -145 },
  { ox: -88, oy: -76 },
  { ox: 88, oy: -76 },
  { ox: -94, oy: 114 },
  { ox: 94, oy: 114 },
  { ox: -80, oy: 168 },
  { ox: 80, oy: 168 },
];
// Mk V Battleship (MTS_V_BTS, 237×512): 14 turret sockets, 7 down each flank of the
// wedge — widest at the superstructure (rear, −oy), tapering to the nose point (+oy).
// oy: +y = toward the nose (texture bottom), matching the hull convention.
export const TURRET_MOUNTS_MKV_BTS = [
  { ox: -95, oy: -127 }, { ox: 95, oy: -127 },
  { ox: -94, oy: -63 }, { ox: 94, oy: -63 },
  { ox: -91, oy: -35 }, { ox: 91, oy: -35 },
  { ox: -84, oy: -8 }, { ox: 84, oy: -8 },
  { ox: -77, oy: 18 }, { ox: 77, oy: 18 },
  { ox: -65, oy: 45 }, { ox: 65, oy: 45 },
  { ox: -57, oy: 74 }, { ox: 57, oy: 74 },
];
// Mk V Swarm-lord (MTS_V_SWL, 402×512): 14 turrets — 2 at the centre (a horizontal
// pair flanking the spine), 3 per swept wing (leading edge, out to the tip), 3 per
// inner flank (the stacked column below each wing root). Measured off the sockets.
// oy: +y = toward the nose (texture bottom); the wings/superstructure sit at −oy.
export const TURRET_MOUNTS_MKV_SWL = [
  { ox: -24, oy: -141 }, { ox: 24, oy: -141 }, // centre pair
  { ox: -66, oy: -133 }, { ox: 66, oy: -133 }, // wing inner
  { ox: -121, oy: -123 }, { ox: 121, oy: -123 }, // wing mid
  { ox: -169, oy: -110 }, { ox: 169, oy: -110 }, // wing tip
  { ox: -78, oy: -31 }, { ox: 78, oy: -31 }, // flank upper
  { ox: -80, oy: -5 }, { ox: 80, oy: -5 }, // flank mid
  { ox: -76, oy: 13 }, { ox: 76, oy: 13 }, // flank lower
];
// Mk VI Battleship (MTS_VI_BTS, 1024×1536 → offsets ÷3 to the 512 reference): 18 turret
// sockets down the flanks; prow at +oy. Mapped in the Mount Mapper on the 1536 art.
export const TURRET_MOUNTS_MKVI_BTS = [
  { ox: -44, oy: -167 }, { ox: 44, oy: -167 },
  { ox: -49, oy: -143 }, { ox: 49, oy: -143 },
  { ox: -53, oy: -62 }, { ox: 53, oy: -62 },
  { ox: -51, oy: -33 }, { ox: 51, oy: -33 },
  { ox: -44, oy: -8 }, { ox: 44, oy: -8 },
  { ox: -28, oy: 72 }, { ox: 28, oy: 72 },
  { ox: -23, oy: 93 }, { ox: 23, oy: 93 },
  { ox: -18, oy: 111 }, { ox: 18, oy: 111 },
  { ox: -16, oy: 134 }, { ox: 16, oy: 134 },
];

// Mk VI Swarm-lord (MTS_VI_SWL 1016×1497 → ÷2.924 to the 512 reference): 11 turrets, from
// MK_VI_SWL_Mapping.txt — 3 per swept wing + 2+2+1 down the inner flanks. oy: +y = prow.
export const TURRET_MOUNTS_MKVI_SWL = [
  { ox: 110.1, oy: -144.0 }, { ox: -110.1, oy: -144.0 },
  { ox: 130.3, oy: -133.4 }, { ox: -130.3, oy: -133.4 },
  { ox: 151.5, oy: -123.8 }, { ox: -151.5, oy: -123.8 },
  { ox: 61.9, oy: -30.8 }, { ox: -61.9, oy: -30.8 },
  { ox: 59.2, oy: -15.7 }, { ox: -59.2, oy: -15.7 },
  { ox: 41.4, oy: 0.7 }, { ox: -41.4, oy: 0.7 }, // centre pair (added the missing left mirror → 12)
];
// Mk VII Swarm-lord turrets (12) — MKVIISWL Mapping.txt on MTS_VII_SWL 1024×1536, ÷3 → 512 ref.
export const TURRET_MOUNTS_MKVII_SWL = [
  { ox: -148.7, oy: -108.3 }, { ox: 148.7, oy: -108.3 },
  { ox: -129.3, oy: -117.3 }, { ox: 129.3, oy: -117.3 },
  { ox: -111.7, oy: -127.0 }, { ox: 111.7, oy: -127.0 },
  { ox: -74.3, oy: -59.3 }, { ox: 74.3, oy: -59.3 },
  { ox: -54.3, oy: -21.3 }, { ox: 54.3, oy: -21.3 },
  { ox: -41.3, oy: -3.3 }, { ox: 41.3, oy: -3.3 },
];

/** The turret mount offsets for a hull tier: Mk VI (18 BTS / 14 SWL) / Mk V (14, by path) / Mk IV / Mk III (8) or Mk II (4). */
export function mountsForTier(
  tier: number,
  mkvPath: "battleship" | "swarmlord" | "none" = "none",
  mkviiSwl = false, // Mk VII Swarm-lord variant (tier-5 swarmlord with the bigger cosmic hull)
): { ox: number; oy: number }[] {
  if (tier >= 6) return []; // Mk VII worm carries its turrets per-segment, not on a hull
  if (tier >= 5)
    return mkvPath === "swarmlord"
      ? mkviiSwl
        ? TURRET_MOUNTS_MKVII_SWL
        : TURRET_MOUNTS_MKVI_SWL
      : TURRET_MOUNTS_MKVI_BTS;
  if (tier >= 4) {
    return mkvPath === "swarmlord" ? TURRET_MOUNTS_MKV_SWL : TURRET_MOUNTS_MKV_BTS;
  }
  return tier >= 3 ? TURRET_MOUNTS_MK4 : tier >= 2 ? TURRET_MOUNTS_MK3 : TURRET_MOUNTS_MK2;
}
const TEX_H = 512; // hull texture height the offsets were measured in

/** Wrap an angle to (-π, π]. */
function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/** Lead a moving target: aim ahead along its velocity so the bolt intercepts it
 * (stationary targets like moon turrets have no velocity → aim directly). Mirrors
 * the spinal cannons' lead, measured from the firing mount. */
function leadTarget(t: Damageable, fromX: number, fromY: number): { x: number; y: number } {
  const vel = (t as { vel?: { x: number; y: number } }).vel;
  if (!vel) return { x: t.pos.x, y: t.pos.y };
  const dist = Math.hypot(t.pos.x - fromX, t.pos.y - fromY);
  const time = Math.min(dist / TURRET.boltSpeed, 1.6); // intercept time, capped
  return { x: t.pos.x + vel.x * time, y: t.pos.y + vel.y * time };
}

export interface TurretDef {
  id: string;
  name: string;
  desc: string;
}
export const TURRETS: TurretDef[] = [
  {
    id: "turret_basic",
    name: "Twin Cannon",
    desc: "Rapid twin-barrel laser turret. Auto-targets the nearest ship.",
  },
];
export const TURRETS_BY_ID: Record<string, TurretDef> = Object.fromEntries(
  TURRETS.map((t) => [t.id, t]),
);

export interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}
export interface TurretState {
  active: boolean;
  mx: number; // mount world x
  my: number; // mount world y
  angle: number; // aim angle (world radians)
  flash: number; // muzzle flash 0..1
  cooldown: number;
  target: Damageable | null; // this mount's own locked target
}

export interface TurretCtx {
  shipX: number;
  shipY: number;
  heading: number;
  renderH: number; // rendered hull height (world units)
  equipped: (string | null)[];
  mountOffsets: { ox: number; oy: number }[]; // tier-specific port positions
  targets: Damageable[]; // neutral miners + hostile raiders
  enabled: boolean; // cannons on/off toggle — mounts stay put but hold fire
  dmgMul?: number; // Gun Commander buff — bolt damage
  fireMul?: number; // Gun Commander buff — fire rate
  turnMul?: number; // Gun Commander buff — turret turn rate
  rangeMul?: number; // Mk V Battleship turret-range upgrades (default 1)
  onHit?: (x: number, y: number) => void; // bolt strike → impact VFX
}

export class TurretSystem {
  // Sized to the maximum port count (Mk VI = 18); lower tiers leave the extras idle.
  readonly mounts: TurretState[] = Array.from({ length: 18 }, () => ({
    active: false,
    mx: 0,
    my: 0,
    angle: 0,
    flash: 0,
    cooldown: Math.random(),
    target: null,
  }));
  readonly bullets: Bullet[] = [];

  reset(): void {
    this.bullets.length = 0;
    for (const m of this.mounts) {
      m.flash = 0;
      m.active = false;
      m.target = null;
    }
  }

  update(dt: number, ctx: TurretCtx): void {
    const scale = ctx.renderH / TEX_H;
    const h = ctx.heading;
    const rx = Math.sin(h),
      ry = -Math.cos(h); // texture +x → world
    const fx = Math.cos(h),
      fy = Math.sin(h); // texture +y (forward) → world
    const range = TURRET.range * (ctx.rangeMul ?? 1); // Battleship range upgrades
    const rangeSq = range * range;

    for (let i = 0; i < this.mounts.length; i++) {
      const m = this.mounts[i]!;
      const off = ctx.mountOffsets[i];
      m.flash = Math.max(0, m.flash - dt * 5);
      if (!off) {
        m.active = false;
        m.target = null;
        continue;
      }
      m.mx = ctx.shipX + scale * (off.ox * rx + off.oy * fx);
      m.my = ctx.shipY + scale * (off.ox * ry + off.oy * fy);
      m.active = (ctx.equipped[i] ?? null) !== null;
      if (!m.active) {
        m.target = null;
        continue;
      }
      m.cooldown -= dt;

      // Each mount only covers its own side's 180° (left mounts sweep the left
      // half, right mounts the right half; both reach dead-ahead and astern).
      const arcSign = off.ox < 0 ? 1 : -1; // +1 = left half, -1 = right half
      const onSide = (tx: number, ty: number): boolean =>
        arcSign * norm(Math.atan2(ty - m.my, tx - m.mx) - h) >= -0.03;

      // Keep the locked target while it's alive, in range, and on this side.
      const cur = m.target;
      if (
        cur &&
        (cur.hp <= 0 ||
          !ctx.targets.includes(cur) ||
          (cur.pos.x - m.mx) ** 2 + (cur.pos.y - m.my) ** 2 >
            rangeSq ||
          !onSide(cur.pos.x, cur.pos.y))
      ) {
        m.target = null;
      }
      // Otherwise acquire the nearest target in range on this mount's side.
      if (!m.target) {
        let best: Damageable | null = null;
        let bestSq = rangeSq;
        for (const s of ctx.targets) {
          if (s.hp <= 0) continue;
          const dsq = (s.pos.x - m.mx) ** 2 + (s.pos.y - m.my) ** 2;
          if (dsq < bestSq && onSide(s.pos.x, s.pos.y)) {
            bestSq = dsq;
            best = s;
          }
        }
        m.target = best;
      }

      if (!ctx.enabled) continue; // cannons off → hold fire

      const tgt = m.target;
      let desired = h;
      if (tgt) {
        const aim = leadTarget(tgt, m.mx, m.my); // aim ahead of movers, like the cannons
        desired = Math.atan2(aim.y - m.my, aim.x - m.mx);
      }
      const diff = norm(desired - m.angle);
      const step = TURRET.turnRate * (ctx.turnMul ?? 1) * dt;
      m.angle += diff > step ? step : diff < -step ? -step : diff;
      if (tgt && m.cooldown <= 0 && Math.abs(diff) < 0.2) {
        this.fire(m);
        m.cooldown = TURRET.fireInterval / (ctx.fireMul ?? 1);
      }
    }

    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      for (const s of ctx.targets) {
        if (s.hp <= 0) continue;
        const rr = NPC.collisionRadius + 4;
        if ((s.pos.x - b.x) ** 2 + (s.pos.y - b.y) ** 2 < rr * rr) {
          this.damage(s, TURRET.boltDamage * (ctx.dmgMul ?? 1));
          ctx.onHit?.(b.x, b.y);
          hit = true;
          break;
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }
  }

  private fire(m: TurretState): void {
    const dx = Math.cos(m.angle);
    const dy = Math.sin(m.angle);
    const px = -dy;
    const py = dx; // perpendicular for the twin-barrel spread
    m.flash = 1;
    for (let k = 0; k < TURRET.boltsPerShot; k++) {
      const side = (k * 2 - (TURRET.boltsPerShot - 1)) * TURRET.barrelSpread;
      this.bullets.push({
        x: m.mx + px * side + dx * TURRET.muzzleLen,
        y: m.my + py * side + dy * TURRET.muzzleLen,
        vx: dx * TURRET.boltSpeed,
        vy: dy * TURRET.boltSpeed,
        life: TURRET.boltLife,
      });
    }
  }

  private damage(s: Damageable, amount: number): void {
    let dmg = amount;
    if (s.shield > 0) {
      const a = Math.min(s.shield, dmg);
      s.shield -= a;
      dmg -= a;
    }
    if (dmg > 0) s.hp = Math.max(0, s.hp - dmg);
    s.shieldFlash = 1;
    s.hitTimer = 0;
  }
}
